import { useCallback, type ReactNode } from 'react';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import type { BoardFlag, Clocks, Disposition, EncounterStatus, IdentityStatus } from './types';

/**
 * Triage colours are DATA (er_triage_levels.color, admin-editable), so they
 * can't be Tailwind tokens and must not be hex literals in source. The colour
 * is set as a CSS custom property on the element through a ref — not a
 * `style` attribute, which features/** forbids — and the classes read it via
 * `var(--lvl)`. A missing colour falls back to the neutral border token.
 */
function useLevelVar(color: string | null | undefined) {
  return useCallback(
    (el: HTMLElement | null) => {
      if (!el) return;
      if (color) el.style.setProperty('--lvl', color);
      else el.style.removeProperty('--lvl');
    },
    [color]
  );
}

/** A solid vertical band — the board row's left edge. */
export function LevelBand({ color, className }: { color: string | null | undefined; className?: string }) {
  const ref = useLevelVar(color);
  return <span ref={ref} aria-hidden className={cn('block w-1.5 self-stretch rounded-full bg-[var(--lvl,var(--border))]', className)} />;
}

/** The level as a coloured pill with its label. */
export function LevelPill({ color, label, className }: { color: string | null | undefined; label: string | null | undefined; className?: string }) {
  const ref = useLevelVar(color);
  if (!label) return <Badge variant="neutral" className={className}>Not triaged</Badge>;
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-[var(--lvl,var(--border))] px-2 py-[1px] text-[11px] font-semibold text-ink',
        className
      )}
    >
      <span className="h-2 w-2 rounded-full bg-[var(--lvl,var(--border))]" />
      {label}
    </span>
  );
}

/** A selectable level button for the triage form. */
export function LevelChoice({ color, label, selected, suggested, onClick }: {
  color: string; label: string; selected: boolean; suggested: boolean; onClick: () => void;
}) {
  const ref = useLevelVar(color);
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={cn(
        'relative flex min-w-[120px] flex-1 items-center gap-2 rounded-lg border-2 px-3 py-2 text-left text-[13px] font-semibold transition',
        selected ? 'border-[var(--lvl)] bg-[var(--lvl)] text-white' : 'border-[var(--lvl)] bg-card text-ink hover:bg-muted'
      )}
    >
      {!selected && <span className="h-3 w-3 shrink-0 rounded-full bg-[var(--lvl)]" />}
      <span>{label}</span>
      {suggested && (
        <span className={cn('ml-auto rounded-full px-1.5 text-[10px] font-bold uppercase', selected ? 'bg-white/25' : 'bg-muted text-ink-muted')}>
          suggested
        </span>
      )}
    </button>
  );
}

export function minutesLabel(min: number | null | undefined): string {
  if (min === null || min === undefined || !Number.isFinite(min)) return '—';
  const neg = min < 0;
  const m = Math.abs(Math.round(min));
  const s = m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
  return neg ? `-${s}` : s;
}

export function timeLabel(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export const STATUS_LABEL: Record<EncounterStatus, string> = {
  arrived: 'Awaiting triage',
  triaged: 'Waiting for doctor',
  in_treatment: 'Under treatment',
  observation: 'Observation',
  disposition_decided: 'Disposition decided',
  departed: 'Departed',
};

export const DISPOSITION_LABEL: Record<Disposition, string> = {
  discharged: 'Discharged home',
  admitted_ward: 'Admitted — ward',
  admitted_icu: 'Admitted — ICU',
  to_ot: 'Shifted to OT',
  referred_out: 'Referred out',
  lama_dama: 'LAMA / DAMA',
  absconded: 'Absconded',
  lwbs: 'Left without being seen',
  death: 'Death in ED',
  brought_in_dead: 'Brought in dead',
};

export const ARRIVAL_MODE_LABEL: Record<string, string> = {
  walk_in: 'Walk-in', ambulance: 'Ambulance', police: 'Police', referral: 'Referral', good_samaritan: 'Good Samaritan', other: 'Other',
};

export function StatusBadge({ status }: { status: EncounterStatus }) {
  const variant = status === 'arrived' ? 'warn' : status === 'departed' ? 'neutral' : status === 'disposition_decided' ? 'info' : status === 'observation' ? 'caution' : 'success';
  return <Badge variant={variant}>{STATUS_LABEL[status]}</Badge>;
}

export function IdentityBadge({ status }: { status: IdentityStatus }) {
  if (status === 'verified') return null;
  return <Badge variant={status === 'unknown' ? 'danger' : 'warn'}>{status === 'unknown' ? 'Unknown identity' : 'Provisional identity'}</Badge>;
}

// The board's icon vocabulary. Each icon carries a tooltip (title) so a new
// nurse can read the board without a legend.
const FLAG_META: Partial<Record<BoardFlag, { icon: string; title: string; tone: 'red' | 'amber' | 'info' }>> = {
  door_to_doctor_breach: { icon: '⏱', title: 'Door-to-doctor target breached', tone: 'red' },
  retriage_overdue: { icon: '🔁', title: 'Re-triage overdue', tone: 'red' },
  critical_result: { icon: '❗', title: 'Critical result not acknowledged', tone: 'red' },
  observation_breach: { icon: '⌛', title: 'Observation time limit exceeded', tone: 'red' },
  boarding_alert: { icon: '🛏', title: 'Boarding longer than the alert threshold', tone: 'red' },
  consult_overdue: { icon: '📞', title: 'Specialist consult overdue', tone: 'red' },
  stat_med_pending: { icon: '💉', title: 'STAT medication not yet charted', tone: 'amber' },
  observation_warn: { icon: '⌛', title: 'Observation limit approaching', tone: 'amber' },
  boarding: { icon: '🛏', title: 'Boarding — waiting to leave the ED', tone: 'info' },
  consult_pending: { icon: '📞', title: 'Specialist consult pending', tone: 'info' },
  pending_lab: { icon: '🧪', title: 'Lab results pending', tone: 'info' },
  verbal_order_unsigned: { icon: '✍', title: 'Verbal order awaiting countersignature', tone: 'amber' },
  mlc: { icon: '⚖', title: 'Medico-legal case', tone: 'info' },
  mlc_incomplete: { icon: '⚖', title: 'MLC: police intimation not recorded', tone: 'amber' },
  identity_unresolved: { icon: '❓', title: 'Identity not yet verified', tone: 'info' },
};

export function FlagIcons({ flags, className }: { flags: BoardFlag[]; className?: string }) {
  // mlc_incomplete supersedes the plain mlc icon; boarding_alert supersedes boarding.
  const shown = flags.filter((f) => !(f === 'mlc' && flags.includes('mlc_incomplete')) && !(f === 'boarding' && flags.includes('boarding_alert')));
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1', className)}>
      {shown.map((f) => {
        const m = FLAG_META[f];
        if (!m) return null;
        return (
          <span
            key={f}
            title={m.title}
            className={cn(
              'inline-flex h-6 min-w-6 items-center justify-center rounded-md px-1 text-[13px]',
              m.tone === 'red' && 'bg-danger-soft',
              m.tone === 'amber' && 'bg-warn-soft',
              m.tone === 'info' && 'bg-muted'
            )}
          >
            {m.icon}
          </span>
        );
      })}
    </span>
  );
}

/** The door-to-doctor clock as the board shows it. */
export function DoorToDoctor({ clocks, seen }: { clocks: Clocks; seen: boolean }) {
  const d = clocks.door_to_doctor;
  if (!d) return <span className="text-ink-muted">—</span>;
  if (seen) {
    return <span className={cn('text-[12px]', d.met ? 'text-success-ink' : 'text-danger-ink')}>seen at {minutesLabel(d.elapsed_min)}</span>;
  }
  return (
    <span className={cn('font-mono text-[13px] font-semibold', d.breached ? 'text-danger-ink' : (d.remaining_min ?? 99) <= 5 ? 'text-warn-ink' : 'text-ink')}>
      {d.breached ? `+${minutesLabel(-(d.remaining_min ?? 0))} over` : `${minutesLabel(d.remaining_min)} left`}
    </span>
  );
}

export function Section({ title, actions, children, className }: { title: string; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('rounded-xl border border-border bg-card p-4', className)}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[14px] font-semibold text-ink">{title}</h3>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** Turns an ApiError-ish thrown value into a message (+ per-field errors). */
export function errorInfo(e: unknown): { message: string; fields: Record<string, string> } {
  const err = e as { message?: string; details?: Record<string, unknown> } | null;
  const fields: Record<string, string> = {};
  if (err?.details) {
    for (const [k, v] of Object.entries(err.details)) if (typeof v === 'string' && k !== 'code') fields[k] = v;
  }
  return { message: err?.message || 'Something went wrong', fields };
}
